// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file useTableGroupedRows.tsx
 * @input React, StyleX, Icon, Table types + the flat data array
 * @output Exports useTableGroupedRows hook + config/result types
 * @position Grouped-rows plugin; consumed by Table via plugins prop
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Table/index.ts (exports)
 */
import { useCallback, useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../../../theme/tokens.stylex.js";
import { spacingVars, colorVars, fontWeightVars } from "../../../theme/tokens.stylex.js";
import { Icon } from "../../../Icon/index.js";
import { useTranslator } from "../../../i18n/index.js";

// A synthetic group-header row injected into the flattened data. Real rows
// never carry this marker.
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
const GROUP_HEADER = Symbol('tableGroupHeader');
function isGroupHeader(item) {
  return typeof item === 'object' && item !== null && item[GROUP_HEADER] === true;
}

// Proxy handler: any field access beyond the marker fields resolves to `''`
// so user cell renderers (`item.name.toUpperCase()`) never throw on a header.
const HEADER_PROXY_HANDLER = {
  // eslint-disable-next-line @typescript-eslint/promise-function-async -- Proxy get trap, not a promise-returning fn
  get(t, prop) {
    if (prop === GROUP_HEADER || prop === 'groupKey' || prop === 'count') {
      return t[prop];
    }
    return prop in t ? t[prop] : '';
  }
};

/**
 * Build a synthetic header row wrapped in a Proxy so arbitrary field access
 * from user cell renderers (e.g. `item.name.toUpperCase()`) resolves to `''`
 * instead of throwing — BaseTable evaluates `col.renderCell(item)` on every
 * row (including synthetic headers) before `transformBodyRow` can replace the
 * row's cells. `transformBodyRow` then discards those cells and renders a
 * single full-width header cell.
 */
function makeHeader(groupKey, count) {
  const target = {
    [GROUP_HEADER]: true,
    groupKey,
    count
  };
  return new Proxy(target, HEADER_PROXY_HANDLER);
}

/** Configuration for {@link useTableGroupedRows}. */

const styles = {
  headerRow: {
    kkrTdU: "x1ypdohk",
    kfSwDN: "x87ps6o",
    kWkggS: "xwmxj5m",
    kt9PQ7: "xso031l",
    kfdmCh: "x1q0q8m5",
    kL6WhQ: "xw8gpjh",
    $$css: true
  }
};

/**
 * Groups a flat data array into collapsible section rows. Each distinct
 * `groupBy` value becomes a full-width section-header row with a chevron
 * toggle, the group label, and a member count; collapsing hides that group's
 * data rows while keeping the header visible.
 *
 * Mirrors {@link useTableRowExpansionState}: the consumer owns the
 * `collapsedGroups` set and this hook returns `{data, plugin, idKey}` —
 * pass all three to `<Table>`.
 *
 * @example
 * ```
 * const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
 * const grouped = useTableGroupedRows({
 *   data: rows,
 *   groupBy: r => r.team,
 *   collapsedGroups: collapsed,
 *   onToggleGroup: key =>
 *     setCollapsed(prev => {
 *       const next = new Set(prev);
 *       next.has(key) ? next.delete(key) : next.add(key);
 *       return next;
 *     }),
 *   getRowKey: r => r.id,
 * });
 * <Table
 *   data={grouped.data}
 *   columns={columns}
 *   idKey={grouped.idKey}
 *   plugins={{grouped: grouped.plugin}}
 * />;
 * ```
 */
export function useTableGroupedRows(config) {
  const t = useTranslator();
  const {
    data,
    groupBy,
    collapsedGroups,
    onToggleGroup,
    renderGroupHeader,
    getRowKey: getRowKeyProp,
    groupOrder
  } = config;
  const flattened = useMemo(() => {
    if (data.length === 0) {
      return [];
    }

    // Group preserving first-seen order.
    const groups = new Map();
    for (const item of data) {
      const key = groupBy(item);
      const bucket = groups.get(key);
      if (bucket) {
        bucket.push(item);
      } else {
        groups.set(key, [item]);
      }
    }

    // Determine iteration order.
    let keys = [...groups.keys()];
    if (groupOrder && groupOrder.length > 0) {
      const ordered = groupOrder.filter(k => groups.has(k));
      const rest = keys.filter(k => !groupOrder.includes(k));
      keys = [...ordered, ...rest];
    }
    const out = [];
    for (const key of keys) {
      const rows = groups.get(key) ?? [];
      out.push(makeHeader(key, rows.length));
      if (!collapsedGroups.has(key)) {
        out.push(...rows);
      }
    }
    return out;
  }, [data, groupBy, collapsedGroups, groupOrder]);

  // Positional fallback index, built once per flattened array so key lookup
  // stays O(1) instead of O(n) per row (which would make table keying O(n²)).
  const positionByItem = useMemo(() => {
    if (getRowKeyProp) {
      return null;
    }
    const map = new Map();
    for (let i = 0; i < flattened.length; i++) {
      map.set(flattened[i], i);
    }
    return map;
  }, [flattened, getRowKeyProp]);
  const idKey = useCallback(item => {
    if (isGroupHeader(item)) {
      return `__group_${item.groupKey}`;
    }
    if (getRowKeyProp) {
      return getRowKeyProp(item);
    }
    return String(positionByItem?.get(item) ?? -1);
  }, [getRowKeyProp, positionByItem]);
  const plugin = useMemo(() => ({
    // Replace a header row's pre-rendered cells with one full-width cell.
    transformBodyRow(props, item) {
      if (!isGroupHeader(item)) {
        return props;
      }
      const header = item;
      const collapsed = collapsedGroups.has(header.groupKey);
      const toggle = () => onToggleGroup(header.groupKey);
      const content = renderGroupHeader ? renderGroupHeader(header.groupKey, header.count, collapsed) : /*#__PURE__*/_jsxs("span", {
        ...{
          className: "x2mo6ok x1tgivj0"
        },
        children: [header.groupKey, ' ', /*#__PURE__*/_jsxs("span", {
          ...{
            className: "x1sodnla xv1l7n4"
          },
          children: ["(", header.count, ")"]
        })]
      });
      return {
        ...props,
        htmlProps: {
          ...props.htmlProps,
          // Convenience: clicking anywhere on the row toggles it. The chevron
          // button below is the accessible, keyboard-operable control, so the
          // row keeps its implicit `row` role (no role override here).
          onClick: toggle,
          'aria-expanded': !collapsed
        },
        xstyle: [...props.xstyle, styles.headerRow],
        children:
        /*#__PURE__*/
        // colSpan larger than the column count is clamped by the browser
        // to the actual number of columns, so the header always spans the
        // full width without the plugin knowing the column count.
        _jsx("td", {
          colSpan: 999,
          ...{
            className: "xce4md1 x1vsv5vr x1t818jl"
          },
          children: /*#__PURE__*/_jsxs("span", {
            ...{
              className: "x78zum5 x6s0dn4 xzye2dw"
            },
            children: [/*#__PURE__*/_jsx("button", {
              type: "button",
              ...{
                className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x1717udv x1ghz6dp x1ypdohk xv9yike x1cqbx0l"
              },
              onClick: e => {
                e.stopPropagation();
                toggle();
              },
              "aria-label": collapsed ? t('@astryx.tableGroupedRows.expandGroup', {
                groupKey: header.groupKey
              }) : t('@astryx.tableGroupedRows.collapseGroup', {
                groupKey: header.groupKey
              }),
              "aria-expanded": !collapsed,
              children: /*#__PURE__*/_jsx("span", {
                ...{
                  0: {
                    className: "x3nfvp2 x11xpdln xx6bhzk"
                  },
                  1: {
                    className: "x3nfvp2 x11xpdln xx6bhzk x1iffjtl"
                  }
                }[!!!collapsed << 0],
                children: /*#__PURE__*/_jsx(Icon, {
                  icon: "chevronRight",
                  size: "xsm"
                })
              })
            }), content]
          })
        })
      };
    }
  }), [collapsedGroups, onToggleGroup, renderGroupHeader, t]);
  return {
    plugin,
    data: flattened,
    idKey
  };
}